import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtMoney } from "@/lib/format";
import { Card } from "@/components/ui/card";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import { Banknote, ShoppingBag, Scale, Wallet, TrendingUp, AlertCircle, ChevronLeft, ChevronRight, CalendarSearch } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const COLORS = ["#0284c7", "#7c3aed", "#f59e0b", "#10b981", "#ef4444", "#14b8a6", "#6366f1"];
const PERIODS = [{ k: "day", l: "Jour" }, { k: "week", l: "Semaine" }, { k: "month", l: "Mois" }, { k: "year", l: "Année" }];

const Stat = ({ label, value, icon: Icon, color, testid }) => (
  <Card data-testid={testid} className={`p-4 border-0 text-white ${color}`}>
    <div className="flex items-center justify-between">
      <div><p className="text-white/80 text-xs">{label}</p><p className="text-xl font-bold mt-0.5">{value}</p></div>
      <Icon size={26} className="text-white/70" />
    </div>
  </Card>
);

export default function Reports() {
  const { settings } = useAuth();
  const cur = settings?.currency || "KMF";
  const [r, setR] = useState(null);
  const [period, setPeriod] = useState("week");
  const [refDate, setRefDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [pd, setPd] = useState(null);

  useEffect(() => { api.get("/reports").then((res) => setR(res.data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/reports/period", { params: { period, date: refDate } }).then((res) => setPd(res.data)).catch(() => {});
  }, [period, refDate]);

  const shift = (dir) => {
    const d = new Date(refDate + "T00:00:00");
    if (period === "day") d.setDate(d.getDate() + dir);
    else if (period === "week") d.setDate(d.getDate() + dir * 7);
    else if (period === "month") d.setMonth(d.getMonth() + dir);
    else if (period === "year") d.setFullYear(d.getFullYear() + dir);
    setRefDate(d.toISOString().slice(0, 10));
  };

  if (!r) return <p className="text-slate-400">Chargement...</p>;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-800">Rapports</h1>

      <Card className="p-5 border-sky-200 bg-sky-50/50" data-testid="period-report-card">
        <div className="flex items-center gap-2 mb-4">
          <CalendarSearch size={20} className="text-sky-600" />
          <h3 className="font-semibold text-slate-800">Consulter le CA d'une période passée</h3>
        </div>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="flex rounded-lg border border-slate-200 overflow-hidden">
            {PERIODS.map((p) => (
              <button key={p.k} data-testid={`period-${p.k}`} onClick={() => setPeriod(p.k)}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${period === p.k ? "bg-sky-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
                {p.l}
              </button>
            ))}
          </div>
          <Input type="date" data-testid="period-date" value={refDate} onChange={(e) => setRefDate(e.target.value)} className="w-auto" />
          <div className="flex gap-1">
            <Button size="icon" variant="outline" data-testid="period-prev" onClick={() => shift(-1)}><ChevronLeft size={16} /></Button>
            <Button size="icon" variant="outline" data-testid="period-next" onClick={() => shift(1)}><ChevronRight size={16} /></Button>
          </div>
        </div>
        {pd && (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="bg-white rounded-lg p-4 border border-sky-100">
              <p className="text-xs text-slate-500">Période</p>
              <p className="text-sm font-semibold text-slate-800" data-testid="period-label">{pd.label}</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-sky-100">
              <p className="text-xs text-slate-500">Chiffre d'affaires</p>
              <p className="text-xl font-bold text-sky-700" data-testid="period-ca">{fmtMoney(pd.ca, cur)}</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-sky-100">
              <p className="text-xs text-slate-500">Encaissé (théorique)</p>
              <p className="text-xl font-bold text-emerald-700">{fmtMoney(pd.collected, cur)}</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-amber-200">
              <p className="text-xs text-slate-500">Comptabilisé (réel)</p>
              <p className="text-xl font-bold text-amber-700" data-testid="period-real">{fmtMoney(pd.real_accounted, cur)}</p>
              {pd.closings_count === 0
                ? <p className="text-[11px] text-slate-400 mt-0.5">Aucune clôture</p>
                : <p className={`text-[11px] mt-0.5 ${pd.cash_variance === 0 ? "text-slate-400" : pd.cash_variance > 0 ? "text-emerald-600" : "text-rose-600"}`}>Écart caisse : {fmtMoney(pd.cash_variance, cur)}</p>}
            </div>
            <div className="bg-white rounded-lg p-4 border border-rose-200">
              <p className="text-xs text-slate-500">Dépenses (sorties)</p>
              <p className="text-xl font-bold text-rose-700" data-testid="period-expenses">− {fmtMoney(pd.expenses || 0, cur)}</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-sky-100">
              <p className="text-xs text-slate-500">Commandes · Kg</p>
              <p className="text-base font-bold text-slate-700">{pd.orders} · {pd.kg} kg</p>
            </div>
          </div>
        )}
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat testid="rep-ca-day" label="CA réel · jour" value={fmtMoney(r.ca_day, cur)} icon={Banknote} color="bg-sky-600" />
        <Stat testid="rep-ca-week" label="CA réel · semaine" value={fmtMoney(r.ca_week, cur)} icon={TrendingUp} color="bg-indigo-600" />
        <Stat testid="rep-ca-month" label="CA réel · mois" value={fmtMoney(r.ca_month, cur)} icon={TrendingUp} color="bg-violet-600" />
        <Stat testid="rep-ca-year" label="CA réel · année" value={fmtMoney(r.ca_year, cur)} icon={TrendingUp} color="bg-fuchsia-600" />
        <Stat testid="rep-kg-day" label="Kg du jour" value={`${r.kg_day} kg`} icon={Scale} color="bg-teal-600" />
        <Stat testid="rep-kg-week" label="Kg de la semaine" value={`${r.kg_week} kg`} icon={Scale} color="bg-teal-700" />
        <Stat testid="rep-kg-month" label="Kg du mois" value={`${r.kg_month} kg`} icon={Scale} color="bg-cyan-700" />
        <Stat testid="rep-kg-year" label="Kg de l'année" value={`${r.kg_year} kg`} icon={Scale} color="bg-emerald-700" />
        <Stat testid="rep-orders" label="Nb commandes" value={r.total_orders} icon={ShoppingBag} color="bg-slate-700" />
        <Stat testid="rep-kg" label="Kg traités (total)" value={`${r.total_kg} kg`} icon={Scale} color="bg-teal-600" />
        <Stat testid="rep-avg" label="Panier moyen" value={fmtMoney(r.avg_basket, cur)} icon={Wallet} color="bg-emerald-600" />
        <Stat testid="rep-remaining" label="Reste à encaisser" value={fmtMoney(r.remaining_total, cur)} icon={AlertCircle} color="bg-rose-600" />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="p-5">
          <h3 className="font-semibold text-slate-800 mb-3">Revenu réel — 7 derniers jours</h3>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={r.daily_revenue}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="label" fontSize={12} /><YAxis fontSize={12} />
              <Tooltip formatter={(v) => fmtMoney(v, cur)} />
              <Line type="monotone" dataKey="revenue" stroke="#0284c7" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5">
          <h3 className="font-semibold text-slate-800 mb-3">Revenu réel hebdomadaire</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={r.weekly_revenue}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="label" fontSize={12} /><YAxis fontSize={12} />
              <Tooltip formatter={(v) => fmtMoney(v, cur)} />
              <Bar dataKey="revenue" fill="#7c3aed" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5">
          <h3 className="font-semibold text-slate-800 mb-3">Revenu réel mensuel</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={r.monthly_revenue}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="label" fontSize={12} /><YAxis fontSize={12} />
              <Tooltip formatter={(v) => fmtMoney(v, cur)} />
              <Bar dataKey="revenue" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5">
          <h3 className="font-semibold text-slate-800 mb-3">Statut des paiements</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={r.payment_status} dataKey="count" nameKey="status" outerRadius={90} label>
                {r.payment_status.map((e, i) => <Cell key={e.status || i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip /><Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5">
          <h3 className="font-semibold text-slate-800 mb-3">Répartition par service</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={r.best_services} dataKey="revenue" nameKey="name" outerRadius={90} label>
                {r.best_services.map((e, i) => <Cell key={e.name || i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => fmtMoney(v, cur)} /><Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5">
          <h3 className="font-semibold text-slate-800 mb-3">Encaissement par moyen de paiement</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={r.payment_methods_breakdown} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis type="number" fontSize={12} /><YAxis type="category" dataKey="method" fontSize={12} width={90} />
              <Tooltip formatter={(v) => fmtMoney(v, cur)} />
              <Bar dataKey="amount" fill="#f59e0b" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="p-5">
          <h3 className="font-semibold text-slate-800 mb-3">Meilleurs services</h3>
          {r.best_services.slice(0, 6).map((s, i) => (
            <div key={s.name || i} className="flex justify-between py-1.5 border-b border-slate-50 text-sm">
              <span className="text-slate-600">{s.name}</span><span className="font-medium">{fmtMoney(s.revenue, cur)}</span>
            </div>
          ))}
          {r.best_services.length === 0 && <p className="text-slate-400 text-sm">Aucune donnée</p>}
        </Card>

        <Card className="p-5">
          <h3 className="font-semibold text-slate-800 mb-3">Meilleurs clients</h3>
          {r.best_customers.map((c, i) => (
            <div key={c.name || i} className="flex justify-between py-1.5 border-b border-slate-50 text-sm">
              <span className="text-slate-600">{c.name} <span className="text-slate-400">({c.orders})</span></span><span className="font-medium">{fmtMoney(c.revenue, cur)}</span>
            </div>
          ))}
          {r.best_customers.length === 0 && <p className="text-slate-400 text-sm">Aucune donnée</p>}
        </Card>

        <Card className="p-5">
          <h3 className="font-semibold text-slate-800 mb-3">Performance des agents</h3>
          {r.agent_performance.map((a, i) => (
            <div key={a.name || i} className="py-1.5 border-b border-slate-50 text-sm">
              <div className="flex justify-between"><span className="text-slate-600">{a.name}</span><span className="font-medium">{fmtMoney(a.revenue, cur)}</span></div>
              <div className="text-xs text-slate-400">{a.orders} commandes · encaissé {fmtMoney(a.collected, cur)}</div>
            </div>
          ))}
          {r.agent_performance.length === 0 && <p className="text-slate-400 text-sm">Aucune donnée</p>}
        </Card>
      </div>
    </div>
  );
}
